import { Component, Input } from '@angular/core';
import {
  DrawingAdvice,
  DrawingFile,
  DrawingSuggestions,
  drawingComplexityLabel,
} from '../../../models/drawing-file.model';

interface ExtractionInfoField {
  label: string;
  value: string;
  multiline?: boolean;
}

@Component({
  selector: 'app-drawing-extraction-info',
  standalone: true,
  templateUrl: './drawing-extraction-info.component.html',
})
export class DrawingExtractionInfoComponent {
  @Input() drawingFile: DrawingFile | null = null;

  get suggestions(): DrawingSuggestions | null {
    return this.drawingFile?.latest_extraction?.suggestions ?? null;
  }

  get drawingOverview(): string {
    return this.suggestions?.overview?.trim() ?? '';
  }

  get suggestionAdvice(): DrawingAdvice[] {
    return this.suggestions?.advice ?? [];
  }

  get hasReviewNotes(): boolean {
    return Boolean(this.drawingOverview || this.suggestionAdvice.length);
  }

  get fields(): ExtractionInfoField[] {
    const fields: ExtractionInfoField[] = [
      { label: 'Part Number', value: this.partNumber },
      { label: 'Title', value: this.title },
      { label: 'Revision', value: this.revision },
      { label: 'Units', value: this.units },
      { label: 'Dimensions', value: this.dimensionsLabel },
      { label: 'Profile Edge Length', value: this.profileEdgeLength },
      { label: 'Hole Edge Length', value: this.holeEdgeLength },
      { label: 'Material', value: this.material },
      { label: 'Holes', value: this.holes },
      { label: 'Complexity', value: this.complexity },
    ];

    fields.push(
      { label: 'Tolerances', value: this.tolerances, multiline: true },
      { label: 'General Notes', value: this.generalNotes, multiline: true },
    );

    return fields;
  }

  private get partNumber(): string {
    return this.getTextValue('part_number');
  }

  private get title(): string {
    return this.getTextValue('title');
  }

  private get revision(): string {
    return this.getTextValue('revision');
  }

  private get units(): string {
    const confirmedUnits =
      this.drawingFile?.latest_extraction?.confirmed_dimensions?.units?.trim();
    if (confirmedUnits) {
      return confirmedUnits;
    }
    return this.getTextValue('units');
  }

  private get material(): string {
    return this.getTextValue('material');
  }

  private get holes(): string {
    const extracted = this.extractedData['holes'];
    if (extracted === null || extracted === undefined || extracted === '') {
      return '-';
    }
    return String(extracted);
  }

  private get complexity(): string {
    const stored = this.drawingFile?.latest_extraction?.complexity;
    return drawingComplexityLabel(
      stored || this.getRawTextValue('complexity') || undefined,
    );
  }

  private get tolerances(): string {
    return this.getTextValue('tolerances');
  }

  private get dimensionsLabel(): string {
    const dimensions = this.confirmedOrExtractedDimensions;
    const units = this.units === '-' ? '' : ` ${this.units}`;
    if (!dimensions) {
      return '-';
    }
    return `${dimensions.length} × ${dimensions.width} × ${dimensions.depth}${units}`;
  }

  private get profileEdgeLength(): string {
    return this.formatConfirmedEdgeLength('profile_edge_length');
  }

  private get holeEdgeLength(): string {
    return this.formatConfirmedEdgeLength('hole_edge_length');
  }

  private get generalNotes(): string {
    const extracted =
      this.extractedData['general_notes'] ?? this.extractedData['notes'];
    if (extracted === null || extracted === undefined) {
      return '-';
    }
    const value = String(extracted).trim();
    return value || '-';
  }

  private get extractedData(): Record<string, unknown> {
    return (
      (this.drawingFile?.latest_extraction?.extracted_data as Record<
        string,
        unknown
      >) ?? {}
    );
  }

  private get confirmedOrExtractedDimensions(): {
    length: string;
    width: string;
    depth: string;
  } | null {
    const confirmed = this.drawingFile?.latest_extraction?.confirmed_dimensions;
    if (confirmed) {
      return {
        length: String(confirmed.length),
        width: String(confirmed.width),
        depth: String(confirmed.depth),
      };
    }

    const dimensions = this.extractedData['dimensions'];
    if (
      !dimensions ||
      typeof dimensions !== 'object' ||
      Array.isArray(dimensions)
    ) {
      return null;
    }
    const dimensionMap = dimensions as Record<string, unknown>;
    const length = String(dimensionMap['overall_length'] ?? '').trim();
    const width = String(dimensionMap['overall_width'] ?? '').trim();
    const depth = String(dimensionMap['overall_height'] ?? '').trim();

    if (!length && !width && !depth) {
      return null;
    }
    return {
      length: length || '-',
      width: width || '-',
      depth: depth || '-',
    };
  }

  private formatConfirmedEdgeLength(
    key: 'profile_edge_length' | 'hole_edge_length',
  ): string {
    const value =
      this.drawingFile?.latest_extraction?.confirmed_dimensions?.[key];
    if (value == null || !Number.isFinite(value)) {
      return '-';
    }
    return `${value} mm`;
  }

  private getRawTextValue(key: string): string {
    const extracted = this.extractedData[key];
    if (extracted === null || extracted === undefined) {
      return '';
    }
    return String(extracted).trim();
  }

  private getTextValue(key: string): string {
    const value = this.getRawTextValue(key);
    return value || '-';
  }
}
